/**
 * Theme Context
 * Manages light/dark mode theme state with localStorage persistence
 */

import React, { createContext, useContext, useState, useEffect } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextValue {
  theme: Theme;
  toggleTheme: () => void;
  setLightTheme: () => void;
  setDarkTheme: () => void;
  isDark: boolean;
  isLight: boolean;
}

interface ThemeProviderProps {
  children: React.ReactNode;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Initialize theme - always default to light mode
  const [theme, setTheme] = useState<Theme>(() => {
    // Check localStorage first
    const saved = localStorage.getItem('bc-portal-theme');
    if (saved) {
      return saved as Theme;
    }

    // Always default to light mode (dark mode temporarily disabled)
    // System preference check disabled until dark mode is re-enabled in v3.5.0
    // if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    //   return 'dark';
    // }

    return 'light';
  });

  // Apply theme to document
  useEffect(() => {
    // Remove both classes first
    document.documentElement.classList.remove('light-theme', 'dark-theme', 'dark');

    // Add current theme class
    document.documentElement.classList.add(`${theme}-theme`);
    // shadcn/ui components (Card, Dialog, Popover…) read their tokens from `.dark` (index.css),
    // so dark mode must set it too or those surfaces stay white (UX-GROWTH X4).
    if (theme === 'dark') document.documentElement.classList.add('dark');

    // Save to localStorage
    localStorage.setItem('bc-portal-theme', theme);

    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute(
        'content',
        theme === 'dark' ? '#1a1a1a' : '#1976d2'
      );
    }
  }, [theme]);

  // Listen for system theme changes - DISABLED until dark mode re-enabled in v3.5.0
  // useEffect(() => {
  //   const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

  //   const handleChange = (e) => {
  //     // Only update if user hasn't manually set a preference
  //     const saved = localStorage.getItem('bc-portal-theme');
  //     if (!saved) {
  //       setTheme(e.matches ? 'dark' : 'light');
  //     }
  //   };

  //   // Modern browsers
  //   if (mediaQuery.addEventListener) {
  //     mediaQuery.addEventListener('change', handleChange);
  //     return () => mediaQuery.removeEventListener('change', handleChange);
  //   }
  //   // Legacy browsers
  //   else if (mediaQuery.addListener) {
  //     mediaQuery.addListener(handleChange);
  //     return () => mediaQuery.removeListener(handleChange);
  //   }
  // }, []);

  const toggleTheme = (): void => {
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  const setLightTheme = (): void => setTheme('light');
  const setDarkTheme = (): void => setTheme('dark');

  const value: ThemeContextValue = {
    theme,
    toggleTheme,
    setLightTheme,
    setDarkTheme,
    isDark: theme === 'dark',
    isLight: theme === 'light'
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export default ThemeContext;
